/**
 * TypeScript view of the `word_ladder` format.
 * Source of truth: `shared/schema/word_ladder.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;
export type Language = "en" | "fr";

export const LANGUAGES: Language[] = ["en", "fr"];
export const MAX_RUNGS = 59;
export const MAX_STEPS = 60;

export interface WordLadderDefinition {
  puzzleType: "word_ladder";
  typeVersion: number;
  language: Language;
  /** The bottom rung, uppercase ASCII. */
  start: string;
  /** The top rung, same length as `start`. */
  end: string;
  /** Optional and public: the ladder may take at most this many steps. */
  maxSteps?: number;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface WordLadderProgress {
  version: 1;
  /** The rungs between the start and the end word, in order, normalised. */
  rungs: string[];
}

/** The whole ladder, start and end included. */
export interface WordLadderSubmission {
  ladder: string[];
}

/** The author's ladder, proving the climb exists. Never read by the verdict. */
export interface WordLadderSolution {
  ladder: string[];
}

/** What a rung can get wrong. `unknown_word` is only ever the server's to say. */
export type IssueCode =
  | "wrong_start"
  | "wrong_end"
  | "wrong_length"
  | "not_one_letter"
  | "unknown_word"
  | "too_many_steps";

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
